import React, { useEffect, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle, AlertTriangle, ExternalLink, HelpCircle, FileText, ArrowLeft } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { safeJsonLd } from "../lib/safeJsonLd";
import { useCanonical } from "../lib/seoHelpers";
import { getAppealMatrixPair, getRelatedPairs } from "../data/appealMatrix";

/**
 * Long-tail programmatic SEO page: /appeal/:platform/:reason
 *
 * Every URL renders unique, non-thin content sourced from appealMatrix.js.
 * Emits FAQPage + Article + BreadcrumbList JSON-LD to maximize Google
 * rich-result eligibility and AI answer-engine citation.
 */
export default function ProgrammaticAppealPage() {
  const { platform: platformSlug, reason: reasonSlug } = useParams();
  const pair = getAppealMatrixPair(platformSlug, reasonSlug);

  const canonical = pair
    ? `https://socialdisputeco.com/appeal/${platformSlug}/${reasonSlug}`
    : null;
  useCanonical(canonical);

  useEffect(() => {
    if (!pair) return;
    document.title = `${pair.platform.name} ${pair.reason.label} Appeal — Documentation Help | Social Dispute Co`;
    const metaDesc = document.querySelector('meta[name="description"]');
    const desc = `${pair.platform.name} suspended for ${pair.reason.label.toLowerCase()}? Independent appeal documentation help. We prepare, we submit, we follow up. No guarantees, no insider claims.`;
    if (metaDesc) metaDesc.setAttribute("content", desc);
  }, [pair]);

  const related = useMemo(
    () => (pair ? getRelatedPairs(platformSlug, reasonSlug, 6) : []),
    [pair, platformSlug, reasonSlug]
  );

  if (!pair) {
    return <Navigate to="/services" replace />;
  }

  const { platform, reason, body } = pair;

  // JSON-LD schema graph — Article + FAQPage + Breadcrumb
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${platform.name} ${reason.label} — Appeal Documentation Guide`,
    description: reason.tagline,
    author: {
      "@type": "Person",
      name: "Mark Becker",
      url: "https://socialdisputeco.com/about",
    },
    publisher: {
      "@type": "Organization",
      name: "Social Dispute Co",
      logo: {
        "@type": "ImageObject",
        url: "https://socialdisputeco.com/og-image.png",
      },
    },
    datePublished: "2026-02-20",
    dateModified: "2026-02-20",
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://socialdisputeco.com/" },
      { "@type": "ListItem", position: 2, name: platform.name, item: `https://socialdisputeco.com${platform.servicePath}` },
      { "@type": "ListItem", position: 3, name: reason.label, item: canonical },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: `How do I appeal a ${platform.name} ${reason.label.toLowerCase()} suspension?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: body,
        },
      },
      {
        "@type": "Question",
        name: `What's the official ${platform.name} appeal channel?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `${platform.name} publishes their appeal form at ${platform.officialAppealUrl}. This is the same public URL any user can submit through — Social Dispute Co submits here with the client's authorization; we never claim insider access.`,
        },
      },
      {
        "@type": "Question",
        name: `Does Social Dispute Co guarantee ${platform.name} account restoration?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `No. Platform decisions are made entirely by ${platform.parent}'s internal review teams. Social Dispute Co explicitly does not guarantee any outcome. Any service that claims to "guarantee" account restoration is one to avoid.`,
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b]" data-testid="programmatic-appeal-page">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(faqSchema) }}
      />

      <Header />

      <main>
        {/* Breadcrumb strip */}
        <nav className="pt-28 pb-4" aria-label="Breadcrumb">
          <div className="max-w-5xl mx-auto px-5 md:px-10">
            <ol className="flex items-center gap-2 text-xs text-[#888680] flex-wrap">
              <li><Link to="/" className="hover:text-[#d9fb06]">Home</Link></li>
              <li>›</li>
              <li><Link to={platform.servicePath} className="hover:text-[#d9fb06]">{platform.name}</Link></li>
              <li>›</li>
              <li className="text-[#d9fb06]">{reason.label}</li>
            </ol>
          </div>
        </nav>

        {/* Hero */}
        <section className={`pt-6 pb-16 bg-gradient-to-br ${platform.color} relative overflow-hidden`}>
          <div className="absolute inset-0 bg-black/70" />
          <div className="max-w-4xl mx-auto px-5 md:px-10 relative z-10">
            <p className="text-xs uppercase tracking-widest text-[#d9fb06]/80 mb-3 font-mono">
              {platform.name} · {reason.label}
            </p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white uppercase leading-[1.05] mb-5">
              {platform.name} Suspended for {reason.label}?
            </h1>
            <p className="text-lg md:text-xl text-white/85 mb-8 max-w-2xl">
              {reason.tagline}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/analyze">
                <Button
                  size="lg"
                  className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 font-bold px-8"
                  data-testid={`prog-${platformSlug}-${reasonSlug}-analyze-cta`}
                >
                  Free 60-Second Analyzer
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to={platform.servicePath}>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10 font-bold px-8"
                >
                  All {platform.name} Cases
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Intro narrative */}
        <section className="py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-5 md:px-10">
            <p className="text-lg text-[#dfddd6] leading-relaxed">{reason.intro}</p>
          </div>
        </section>

        {/* Platform-specific body — the unique content that keeps this page out of "thin" territory */}
        <section className="py-12 md:py-16 bg-[#302f2c]" data-testid="prog-body">
          <div className="max-w-3xl mx-auto px-5 md:px-10">
            <h2 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase mb-6">
              How {platform.name} handles {reason.label.toLowerCase()}
            </h2>
            <p className="text-[#dfddd6] leading-relaxed text-base md:text-lg">{body}</p>

            <div className="mt-8 rounded-xl border border-[#3f4816]/40 bg-[#1a1c1b] p-5">
              <p className="text-xs uppercase tracking-wider text-[#888680] mb-2">
                Official {platform.name} appeal channel
              </p>
              <a
                href={platform.officialAppealUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#d9fb06] hover:underline text-sm font-mono break-all"
                data-testid="prog-official-appeal-link"
              >
                {platform.officialAppealUrl}
                <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
              </a>
              <p className="text-xs text-[#888680] mt-2">
                This is the same public appeal URL anyone can submit through. We submit here on your authorization.
              </p>
            </div>
          </div>
        </section>

        {/* Documentation-preparation checklist */}
        <section className="py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-5 md:px-10">
            <h2 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase mb-6">
              What we prepare for a {reason.label} appeal
            </h2>
            <ul className="space-y-3">
              {[
                `Timeline reconstruction of the flagged content or activity on ${platform.name}`,
                `Mapping to the specific ${platform.parent} policy sub-category that reviewers work from`,
                `Evidence organization: screenshots, receipts, prior appeal correspondence`,
                `Identity-verification documents (government ID, business filings, employer letters) where relevant`,
                `A structured appeal narrative written to ${platform.name} reviewer expectations`,
                `Submission through the official ${platform.parent} appeal channel with your authorization`,
                `Follow-up support if additional information is requested or the initial appeal is denied`,
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-[#dfddd6]">
                  <CheckCircle className="w-5 h-5 text-[#d9fb06] flex-shrink-0 mt-0.5" />
                  <span className="text-sm md:text-base leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Compliance notice */}
        <section className="py-8 bg-[#302f2c] border-y border-[#3f4816]/40">
          <div className="max-w-3xl mx-auto px-5 md:px-10">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-[#d9fb06] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#888680] leading-relaxed">
                Social Dispute Co is <strong>not affiliated with {platform.name}</strong>, {platform.parent}, or any other platform.
                We are an independent documentation-preparation service. We do <strong>not guarantee outcomes</strong>. Platform
                decisions are made entirely by {platform.parent}&apos;s internal review teams.
              </p>
            </div>
          </div>
        </section>

        {/* Related topics — internal linking hub */}
        {related.length > 0 && (
          <section className="py-12 md:py-16" data-testid="prog-related">
            <div className="max-w-5xl mx-auto px-5 md:px-10">
              <h2 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase mb-3 flex items-center gap-3">
                <HelpCircle className="w-7 h-7" />
                Related appeal guides
              </h2>
              <p className="text-sm text-[#888680] mb-8">
                Other {platform.name} scenarios and {reason.label.toLowerCase()} cases across platforms.
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                {related.map((p) => (
                  <Link
                    key={`${p.platform.slug}-${p.reason.slug}`}
                    to={`/appeal/${p.platform.slug}/${p.reason.slug}`}
                    className="rounded-xl border border-[#3f4816]/40 bg-[#302f2c] p-4 hover:border-[#d9fb06]/50 transition group flex items-start gap-3"
                  >
                    <FileText className="w-5 h-5 text-[#d9fb06] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[#dfddd6] font-semibold group-hover:text-[#d9fb06] transition text-sm">
                        {p.platform.name} · {p.reason.label}
                      </p>
                      <p className="text-xs text-[#888680] mt-1">Read the guide →</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-16 bg-[#302f2c]">
          <div className="max-w-2xl mx-auto px-5 md:px-10 text-center">
            <h2 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase mb-4">
              Need help preparing your {platform.name} appeal?
            </h2>
            <p className="text-[#888680] mb-6">
              Start with our free 60-second analyzer, or jump straight to a full documentation package.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/analyze">
                <Button size="lg" className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 font-bold px-8">
                  Free Analyzer
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="border-[#d9fb06] text-[#d9fb06] hover:bg-[#d9fb06]/10 font-bold px-8">
                  See Pricing
                </Button>
              </Link>
            </div>
              <p className="mt-6">
                <Link to={platform.servicePath} className="text-sm text-[#888680] hover:text-[#d9fb06] inline-flex items-center gap-1">
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back to all {platform.name} appeals
                </Link>
              </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
